import { camelCase } from "lodash";

import type { CustomProperties } from "../../events/types";
import type { ViewportCallback } from "../types";

/**
 * Viewport observer for tracking element visibility.
 */
class ViewportObserver {
  private readonly nameAttribute: string;
  private readonly customAttribute: string;

  private intersectionObserver: IntersectionObserver | null = null;
  private mutationObserver: MutationObserver | null = null;

  /**
   * Creates a new ViewportObserver instance.
   * @param nameAttribute - HTML attribute name for element identification
   * @param customAttribute - HTML attribute name for custom properties
   */
  public constructor(nameAttribute: string, customAttribute: string) {
    this.nameAttribute = nameAttribute;
    this.customAttribute = customAttribute;
  }

  /**
   * Creates intersection and mutation observers to track element visibility.
   * @param callback - Function to call when elements enter viewport
   * @returns void
   */
  public createObserver = (callback: ViewportCallback): void => {
    if (this.intersectionObserver || this.mutationObserver) {
      this.destroyObserver();
    }

    this.createIntersectionObserver(callback);
    this.createMutationObserver();
  };

  /**
   * Destroys both intersection and mutation observers.
   * @returns void
   */
  public destroyObserver = () => {
    this.destroyIntersectionObserver();
    this.destroyMutationObserver();
  };

  /**
   * Creates an IntersectionObserver to detect when tracked elements enter the viewport.
   * @param callback - Function to call when elements become visible
   * @returns void
   */
  private createIntersectionObserver = (callback: ViewportCallback): void => {
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && callback) {
          const elementName = entry.target.getAttribute(this.nameAttribute);
          const customProperties = this.extractCustomProperties(entry.target);

          if (!elementName) return;

          callback(elementName, customProperties);
        }
      });
    });
  };

  /**
   * Creates a MutationObserver to detect dynamically added elements with tracking attributes.
   * @returns void
   */
  private createMutationObserver = (): void => {
    if (!this.intersectionObserver) return;

    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;

          if (node.hasAttribute(this.nameAttribute)) {
            this.intersectionObserver!.observe(node);
          }

          const descendants = node.querySelectorAll(`[${this.nameAttribute}]`);

          descendants.forEach((descendant) => {
            this.intersectionObserver!.observe(descendant);
          });
        });
      });
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  };

  /**
   * Extracts custom properties from element dataset based on custom attribute prefix.
   * @param element - DOM element to extract properties from
   * @returns CustomProperties
   */
  private extractCustomProperties = (element: Element): CustomProperties => {
    const customProperties: CustomProperties = {};

    if (!(element instanceof HTMLElement)) {
      return customProperties;
    }

    Object.keys(element.dataset).forEach((key) => {
      const customPropertiesPrefix = camelCase(
        this.customAttribute.replace("data-", "")
      );

      if (key.includes(customPropertiesPrefix)) {
        const customKey = key.replace(customPropertiesPrefix, "");

        if (customKey) {
          customProperties[customKey] = element.dataset[key];
        }
      }
    });

    return customProperties;
  };

  /**
   * Disconnects and cleans up the IntersectionObserver instance.
   * @returns void
   */
  private destroyIntersectionObserver = (): void => {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  };

  /**
   * Disconnects and cleans up the MutationObserver instance.
   * @returns void
   */
  private destroyMutationObserver = (): void => {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  };
}

export { ViewportObserver };
